/**
 * 渠道中心 · API 集成中心 · 代理机管理（LIST-3a · ④）——新增 / 重新生成令牌 / 吊销。
 * 真契约源：apps/api-server/src/api_server/routes/listing_jobs.py（管理员那组端点）
 *           designs/mockups/listing-agent/API_MAP.md（tag list3-mockup-v1）
 *
 * 🔴 令牌只在新增/重新生成的响应里出现一次，关闭弹窗后再也拿不到——弹窗内容不能
 * 存进任何 state 之外的地方（不进 localStorage/query cache），组件卸载即丢。
 * listing_agent 开关关着——整块不渲染（工单 §10），由调用方（ChannelCenterPage）
 * 用 `useListingAgentEnabled()` 判断后决定要不要渲染本组件。
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createListingAgent, listListingAgents, revokeListingAgent, rotateListingAgentToken } from "@/services/listingJobs";
import type { ListingAgent, ListingAgentWithToken } from "@/services/types/listingJobs";

function Pill({ tone, children }: { tone: "green" | "gray" | "red"; children: React.ReactNode }) {
  const cls = { green: "bg-emerald-50 text-emerald-600", gray: "bg-slate-100 text-slate-600", red: "bg-rose-50 text-rose-600" }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "从未";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "刚刚";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} 秒前`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

/** 一次性令牌弹窗——新增/重新生成都用这个，关闭后组件卸载，令牌不留任何痕迹。 */
function TokenRevealModal({ name, token, onClose }: { name: string; token: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <div className="border-b border-slate-200 px-5 py-4 font-semibold text-slate-900">代理机「{name}」令牌</div>
        <div className="flex flex-col gap-3 px-5 py-4">
          <p className="text-[13px] text-slate-500">复制到代理机的配置里。关闭这个弹窗后就再也看不到这个令牌了——只有系统里的哈希，丢了只能重新生成。</p>
          <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2">
            <code className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-[12.5px] text-slate-800">{token}</code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(token); setCopied(true); window.setTimeout(() => setCopied(false), 1500); }}
              className="shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600"
            >
              {copied ? "已复制 ✓" : "复制"}
            </button>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800">
            ⚠️ 关闭后无法再次查看——请现在就复制好。
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            我已复制，关闭
          </button>
        </div>
      </div>
    </div>
  );
}

function NewAgentModal({ onClose, onCreated }: { onClose: () => void; onCreated: (agent: ListingAgentWithToken) => void }) {
  const [name, setName] = useState("");
  const mut = useMutation({
    mutationFn: () => createListingAgent(name.trim()),
    onSuccess: (agent) => onCreated(agent),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <div className="border-b border-slate-200 px-5 py-4 font-semibold text-slate-900">新增代理机</div>
        <div className="flex flex-col gap-2 px-5 py-4">
          <label className="text-[12px] font-medium text-slate-600">名称</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="如：办公室电脑"
            className="rounded-md border border-slate-300 px-3 py-2 text-[13px] outline-none focus:border-blue-500"
          />
          {mut.isError && <p className="text-[12px] font-medium text-rose-600">创建失败（名称可能已被占用），请重试</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">取消</button>
          <button
            type="button"
            disabled={!name.trim() || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {mut.isPending ? "创建中…" : "创建"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgentRow({ agent, onRevealToken }: { agent: ListingAgent; onRevealToken: (agent: ListingAgentWithToken) => void }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["listing-agents", "manage"] });
  const rotateMut = useMutation({ mutationFn: () => rotateListingAgentToken(agent.id), onSuccess: (a) => { onRevealToken(a); invalidate(); } });
  const revokeMut = useMutation({ mutationFn: () => revokeListingAgent(agent.id), onSuccess: invalidate });

  const gyg = agent.sessions.gyg;
  const revoked = !!agent.revoked_at;

  return (
    <tr className="border-b border-slate-100">
      <td className="py-2.5 font-semibold text-slate-900">{agent.name}</td>
      <td>{revoked ? <Pill tone="red">已吊销</Pill> : agent.online ? <Pill tone="green">在线</Pill> : <Pill tone="gray">离线</Pill>}</td>
      <td className="text-slate-600">
        GYG {gyg?.logged_in ? <b className="text-emerald-600">有效</b> : <b className="text-slate-400">未登录 / 过期</b>}
      </td>
      <td className="text-slate-500">{agent.version ?? "—"}</td>
      <td className="text-slate-500">{relativeTime(agent.last_seen_at)}</td>
      <td className="text-[12.5px] text-slate-400">{new Date(agent.token_created_at).toLocaleDateString("zh-CN")} 生成 · {agent.token_created_by_name ?? "—"}</td>
      <td className="whitespace-nowrap">
        {!revoked && (
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={rotateMut.isPending}
              onClick={() => { if (window.confirm(`重新生成「${agent.name}」的令牌？旧令牌会立刻失效。`)) rotateMut.mutate(); }}
              className="rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
            >
              重新生成令牌
            </button>
            <button
              type="button"
              disabled={revokeMut.isPending}
              onClick={() => { if (window.confirm(`确认吊销「${agent.name}」？之后它的所有调用都会 401，它领着的任务会转回排队。`)) revokeMut.mutate(); }}
              className="rounded-md border border-rose-200 px-2.5 py-1 text-[12px] font-medium text-rose-600 hover:bg-rose-50"
            >
              吊销
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export function ListingAgentsBlock() {
  const [showNew, setShowNew] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; token: string } | null>(null);

  const agentsQ = useQuery({ queryKey: ["listing-agents", "manage"], queryFn: listListingAgents });
  const qc = useQueryClient();
  const agents = agentsQ.data ?? [];

  return (
    <div className="mt-5 rounded-[14px] border border-slate-200 bg-white p-5">
      <h3 className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
        代理机
        <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10.5px] font-bold text-white">新</span>
      </h3>
      <div className="mb-3.5 mt-1 text-[12px] text-slate-400">
        跑自动建壳程序的电脑。每台一个代理令牌：只显示一次，系统里只存摘要；GYG 账号密码和验证码密钥只存在代理机本地，不经过系统。
      </div>

      {agentsQ.isLoading ? (
        <div className="p-6 text-center text-[13px] text-slate-500">加载中...</div>
      ) : agentsQ.isError ? (
        <button type="button" onClick={() => agentsQ.refetch()} className="w-full rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">
          加载失败，点击重试
        </button>
      ) : agents.length === 0 ? (
        <div className="p-6 text-center text-[13px] text-slate-400">还没有代理机</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
                <th className="py-2">名称</th><th>状态</th><th>平台登录</th><th>版本</th><th>最近心跳</th><th>令牌</th><th></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <AgentRow key={a.id} agent={a} onRevealToken={(withToken) => setRevealed({ name: withToken.name, token: withToken.token })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-blue-700"
        >
          + 新增代理机
        </button>
        <span className="text-[12px] text-slate-400">生成后弹窗显示一次令牌，复制到代理机的配置里；关闭弹窗后再也看不到</span>
      </div>

      {showNew && (
        <NewAgentModal
          onClose={() => setShowNew(false)}
          onCreated={(agent) => {
            setShowNew(false);
            setRevealed({ name: agent.name, token: agent.token });
            qc.invalidateQueries({ queryKey: ["listing-agents", "manage"] });
          }}
        />
      )}
      {revealed && <TokenRevealModal name={revealed.name} token={revealed.token} onClose={() => setRevealed(null)} />}
    </div>
  );
}
